import { useLocalStore } from 'mobx-react-lite';

import { PayloadType } from 'APP/model/message/messageModel.types';
import Entities from 'STORE';
import { Group } from 'STORE/Groups/Group';
import { InputPanel } from 'STORE/InputPanelsStore/InputPanel/InputPanel';

import { editScheduledTextMessage } from '../Tasks/editScheduledTextMessage';
import { editScheduledVoiceMessage } from '../Tasks/editScheduledVoiceMessage';
import { scheduleTextMessage } from '../Tasks/scheduleTextMessage';
import { scheduleVoiceMessage } from '../Tasks/scheduleVoiceMessage';

interface IScheduleButtonPresenter {
  isSubmitDisabled: boolean;
  inputPanel: InputPanel | null;
  group: Group | null;
  isEditMode: boolean;
  isDisabled: boolean;
  isVoiceMode: boolean;
  editMessage(): Promise<void>;
  scheduleMessage(): Promise<void>;
  onSubmit(): Promise<void>;
}

export const useScheduleButtonPresenter = (): IScheduleButtonPresenter => {
  const presenter = useLocalStore<IScheduleButtonPresenter>(() => ({
    isSubmitDisabled: false,

    get inputPanel(): InputPanel | null {
      return Entities.InputPanelsStore.activeInputPanel;
    },

    get group(): Group | null {
      return Entities.GroupsStore.activeGroup;
    },

    get isEditMode(): boolean {
      return presenter.inputPanel?.isEditMode || false;
    },

    get isDisabled(): boolean {
      if (!presenter.inputPanel || presenter.isSubmitDisabled) {
        return true;
      }

      return !presenter.isVoiceMode && presenter.inputPanel.isEmptyValue;
    },

    get isVoiceMode(): boolean {
      if (
        presenter.inputPanel?.editMessage &&
        presenter.inputPanel.editMessage.payload.payloadType === PayloadType.VoiceMessage
      ) {
        return true;
      }

      return Boolean(
        presenter.inputPanel?.voice?.isRecording || presenter.inputPanel?.voice?.isPreviewMode
      );
    },

    async editMessage(): Promise<void> {
      if (!presenter.group || !presenter.inputPanel?.editMessage) {
        return;
      }

      if (presenter.isVoiceMode) {
        await editScheduledVoiceMessage(presenter.group.id, presenter.inputPanel.editMessage);
      } else {
        await editScheduledTextMessage(presenter.group.id, presenter.inputPanel.editMessage);
      }
    },

    async scheduleMessage(): Promise<void> {
      if (!presenter.group) {
        return;
      }

      if (presenter.isVoiceMode) {
        await scheduleVoiceMessage(presenter.group.id);
      } else {
        await scheduleTextMessage(presenter.group.id);
      }
    },

    async onSubmit(): Promise<void> {
      presenter.isSubmitDisabled = true;

      if (presenter.isEditMode) {
        await presenter.editMessage();
      } else {
        await presenter.scheduleMessage();
      }

      presenter.isSubmitDisabled = false;
    },
  }));

  return presenter;
};
